import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, LinearProgress, Alert, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails, FormControlLabel,
  Checkbox, Divider, Rating,
} from '@mui/material';
import {
  TrendingDown, Add, Edit, Delete, Close, CheckCircle, Warning,
  ExpandMore, Flag, EventNote, TrackChanges, Person,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, any> = {
  draft: 'default', active: 'warning', extended: 'info',
  passed: 'success', failed: 'error', withdrawn: 'default',
};

const OUTCOME_LABELS: Record<string, string> = {
  passed: '✅ Passed — Confirmed in role',
  failed_termination: '❌ Failed — Termination',
  failed_demotion: '⬇️ Failed — Demotion',
  extended: '⏱️ Extended',
  withdrawn: '↩️ Withdrawn',
};

const GOAL_STATUS_COLORS: Record<string, any> = {
  not_started: 'default', in_progress: 'info', met: 'success', not_met: 'error',
};

const CHECKIN_RATING_COLORS: Record<string, any> = {
  satisfactory: 'success', needs_improvement: 'warning', unsatisfactory: 'error',
};

const DURATION_OPTIONS = [30, 45, 60, 90, 120];

function daysLeft(endDate: string): number {
  return Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000);
}

function fmtDate(s?: string) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function pctElapsed(startDate: string, endDate: string): number {
  const total = new Date(endDate).getTime() - new Date(startDate).getTime();
  const elapsed = Date.now() - new Date(startDate).getTime();
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface PipGoal {
  id: string; pipId: string; goalTitle: string; description: string;
  measurableTarget?: string; targetDate?: string; status: string; weight: number; progressNotes?: string;
}
interface PipCheckIn {
  id: string; pipId: string; checkInDate: string; rating: string;
  progressSummary: string; concernsRaised?: string; actionItems?: string;
  employeePresent: boolean; employeeAgreed: boolean;
}
interface Pip {
  id: string; employeeId: string; hrOwnerId?: string; managerId?: string;
  title: string; reasonForPip: string; startDate: string; endDate: string;
  durationDays: number; status: string; outcome?: string; outcomeDate?: string; outcomeNotes?: string;
  employeeAcknowledged: boolean; acknowledgedAt?: string; employeeComments?: string; supportProvided?: string;
  employee?: any; manager?: any; hrOwner?: any;
  goals?: PipGoal[]; checkIns?: PipCheckIn[];
}
interface Stats { total: number; active: number; passed: number; failed: number; draft: number; overdue: number; }

function StatCard({ label, value, color, icon }: any) {
  return (
    <Card sx={{ textAlign: 'center', p: 1 }}>
      <CardContent>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

// ── Pip Detail Dialog ─────────────────────────────────────────────────────────

function PipDetail({ pip, onClose, onRefresh, employees, isHR, isManager, currentEmpId }: any) {
  const [goalDialog, setGoalDialog] = useState<Partial<PipGoal> | null>(null);
  const [checkInDialog, setCheckInDialog] = useState<Partial<PipCheckIn> | null>(null);
  const [outcomeDialog, setOutcomeDialog] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [ackComment, setAckComment] = useState('');
  const [ackDialog, setAckDialog] = useState(false);
  const [error, setError] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const canEdit = isHR || isManager;
  const isOwner = currentEmpId === pip.employeeId;
  const isActive = pip.status === 'active' || pip.status === 'extended';

  const saveGoal = async () => {
    if (!goalDialog) return;
    try {
      goalDialog.id
        ? await apiFetch(`/api/pip/goals/${goalDialog.id}`, { method: 'PATCH', body: JSON.stringify(goalDialog) })
        : await apiFetch('/api/pip/goals', { method: 'POST', body: JSON.stringify({ ...goalDialog, pipId: pip.id }) });
      setGoalDialog(null); onRefresh();
    } catch (e: any) { setError(e.message); }
  };

  const deleteGoal = async (id: string) => {
    if (!window.confirm('Remove this goal?')) return;
    try { await apiFetch(`/api/pip/goals/${id}`, { method: 'DELETE' }); onRefresh(); }
    catch (e: any) { setError(e.message); }
  };

  const saveCheckIn = async () => {
    if (!checkInDialog) return;
    try {
      checkInDialog.id
        ? await apiFetch(`/api/pip/checkins/${checkInDialog.id}`, { method: 'PATCH', body: JSON.stringify(checkInDialog) })
        : await apiFetch('/api/pip/checkins', { method: 'POST', body: JSON.stringify({ ...checkInDialog, pipId: pip.id }) });
      setCheckInDialog(null); onRefresh();
    } catch (e: any) { setError(e.message); }
  };

  const deleteCheckIn = async (id: string) => {
    if (!window.confirm('Remove check-in?')) return;
    try { await apiFetch(`/api/pip/checkins/${id}`, { method: 'DELETE' }); onRefresh(); }
    catch (e: any) { setError(e.message); }
  };

  const recordOutcome = async () => {
    try {
      await apiFetch(`/api/pip/${pip.id}/outcome`, { method: 'POST', body: JSON.stringify({ outcome, notes: outcomeNotes }) });
      setOutcomeDialog(false); onRefresh();
    } catch (e: any) { setError(e.message); }
  };

  const activate = async () => {
    try { await apiFetch(`/api/pip/${pip.id}/activate`, { method: 'POST' }); onRefresh(); }
    catch (e: any) { setError(e.message); }
  };

  const acknowledge = async () => {
    try {
      await apiFetch(`/api/pip/${pip.id}/acknowledge`, { method: 'POST', body: JSON.stringify({ comments: ackComment }) });
      setAckDialog(false); onRefresh();
    } catch (e: any) { setError(e.message); }
  };

  const pct = isActive ? pctElapsed(pip.startDate, pip.endDate) : 0;
  const days = isActive ? daysLeft(pip.endDate) : 0;
  const metGoals = (pip.goals ?? []).filter((g: PipGoal) => g.status === 'met').length;
  const totalGoals = (pip.goals ?? []).length;

  return (
    <Dialog open maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle sx={{ bgcolor: '#fff3e0', borderBottom: '1px solid #ffe0b2' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{pip.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {pip.employee?.firstName} {pip.employee?.lastName} ·{' '}
              {fmtDate(pip.startDate)} → {fmtDate(pip.endDate)} ({pip.durationDays}d)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip label={pip.status} color={STATUS_COLORS[pip.status] ?? 'default'} />
            <IconButton onClick={onClose}><Close /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Progress bar */}
        {isActive && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Time elapsed: {pct}%</Typography>
              <Typography variant="body2" color={days <= 14 ? 'error.main' : 'text.secondary'} fontWeight={600}>
                {days > 0 ? `${days} days remaining` : `${Math.abs(days)} days overdue`}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={pct}
              color={pct > 80 ? 'error' : pct > 50 ? 'warning' : 'success'} sx={{ height: 8, borderRadius: 1 }} />
          </Box>
        )}

        {/* Summary row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Manager</Typography>
            <Typography variant="body2" fontWeight={600}>{pip.manager ? `${pip.manager.firstName} ${pip.manager.lastName}` : '—'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">HR Owner</Typography>
            <Typography variant="body2" fontWeight={600}>{pip.hrOwner ? `${pip.hrOwner.firstName} ${pip.hrOwner.lastName}` : '—'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Goals Met</Typography>
            <Typography variant="body2" fontWeight={600} color={totalGoals > 0 && metGoals === totalGoals ? 'success.main' : undefined}>
              {metGoals} / {totalGoals}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Check-ins</Typography>
            <Typography variant="body2" fontWeight={600}>{(pip.checkIns ?? []).length}</Typography>
          </Grid>
        </Grid>

        {/* Reason */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#fff8e1', borderRadius: 2, borderLeft: '4px solid #f57c00' }}>
          <Typography variant="caption" fontWeight={700} color="#e65100">Reason for PIP</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{pip.reasonForPip}</Typography>
        </Box>

        {pip.supportProvided && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
            <Typography variant="caption" fontWeight={700} color="success.dark">Support Provided</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{pip.supportProvided}</Typography>
          </Box>
        )}

        {/* Employee acknowledgement */}
        {pip.employeeAcknowledged ? (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <CheckCircle sx={{ color: 'success.main' }} />
            <Typography variant="body2">Employee acknowledged on {fmtDate(pip.acknowledgedAt)}</Typography>
          </Box>
        ) : isOwner && pip.status !== 'draft' ? (
          <Alert severity="warning" sx={{ mb: 2 }} action={
            <Button size="small" onClick={() => setAckDialog(true)}>Acknowledge</Button>
          }>Please review and acknowledge this PIP plan.</Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>Awaiting employee acknowledgement.</Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Goals */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              <TrackChanges sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />Goals & Targets
            </Typography>
            {canEdit && pip.status !== 'passed' && pip.status !== 'failed' && (
              <Button size="small" startIcon={<Add />}
                onClick={() => setGoalDialog({ status: 'not_started', weight: 0 })}>Add Goal</Button>
            )}
          </Box>

          {(pip.goals ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No goals defined yet.</Typography>
          ) : (
            (pip.goals ?? []).map((g: PipGoal) => (
              <Box key={g.id} sx={{ mb: 1.5, p: 2, border: '1px solid #e0e0e0', borderRadius: 2,
                borderLeft: `4px solid ${g.status === 'met' ? '#2e7d32' : g.status === 'not_met' ? '#c62828' : g.status === 'in_progress' ? '#1565c0' : '#90a4ae'}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                      <Typography fontWeight={700}>{g.goalTitle}</Typography>
                      {g.weight > 0 && <Chip label={`${g.weight}%`} size="small" />}
                      <Chip label={g.status.replace('_', ' ')} size="small" color={GOAL_STATUS_COLORS[g.status]} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">{g.description}</Typography>
                    {g.measurableTarget && (
                      <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
                        🎯 {g.measurableTarget}
                      </Typography>
                    )}
                    {g.targetDate && (
                      <Typography variant="caption" color="text.secondary">Due: {fmtDate(g.targetDate)}</Typography>
                    )}
                    {g.progressNotes && (
                      <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>{g.progressNotes}</Typography>
                    )}
                  </Box>
                  {canEdit && (
                    <Box>
                      <IconButton size="small" onClick={() => setGoalDialog({ ...g })}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteGoal(g.id)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Check-ins */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              <EventNote sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 18 }} />Check-in Log
            </Typography>
            {canEdit && isActive && (
              <Button size="small" startIcon={<Add />}
                onClick={() => setCheckInDialog({ checkInDate: today, rating: 'satisfactory', employeePresent: true, employeeAgreed: false })}>
                Add Check-in
              </Button>
            )}
          </Box>

          {(pip.checkIns ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No check-ins recorded yet.</Typography>
          ) : (
            [...(pip.checkIns ?? [])].sort((a, b) => b.checkInDate.localeCompare(a.checkInDate)).map((ci: PipCheckIn) => (
              <Box key={ci.id} sx={{ mb: 1.5, p: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700}>{fmtDate(ci.checkInDate)}</Typography>
                      <Chip label={ci.rating.replace('_', ' ')} size="small" color={CHECKIN_RATING_COLORS[ci.rating]} />
                      {ci.employeePresent && <Chip label="Employee present" size="small" variant="outlined" />}
                      {ci.employeeAgreed && <Chip label="Agreed" size="small" color="success" variant="outlined" />}
                    </Box>
                    <Typography variant="body2">{ci.progressSummary}</Typography>
                    {ci.concernsRaised && <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>⚠️ {ci.concernsRaised}</Typography>}
                    {ci.actionItems && <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>📋 {ci.actionItems}</Typography>}
                  </Box>
                  {canEdit && (
                    <Box>
                      <IconButton size="small" onClick={() => setCheckInDialog({ ...ci })}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteCheckIn(ci.id)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Outcome */}
        {pip.outcome && (
          <Box sx={{ mt: 2, p: 2, bgcolor: pip.status === 'passed' ? '#e8f5e9' : pip.status === 'failed' ? '#ffebee' : '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700}>Outcome: {OUTCOME_LABELS[pip.outcome]}</Typography>
            {pip.outcomeDate && <Typography variant="body2">Date: {fmtDate(pip.outcomeDate)}</Typography>}
            {pip.outcomeNotes && <Typography variant="body2">{pip.outcomeNotes}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
        {pip.status === 'draft' && canEdit && (
          <Button variant="contained" color="warning" onClick={activate}>Activate PIP</Button>
        )}
        {isActive && canEdit && !pip.outcome && (
          <Button variant="outlined" color="error" onClick={() => setOutcomeDialog(true)}>Record Outcome</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Goal dialog */}
      <Dialog open={!!goalDialog} onClose={() => setGoalDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{goalDialog?.id ? 'Edit Goal' : 'Add Goal'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Goal Title" fullWidth required value={goalDialog?.goalTitle ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, goalTitle: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={2} value={goalDialog?.description ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, description: e.target.value }))} />
            <TextField label="Measurable Target / Success Criteria" fullWidth value={goalDialog?.measurableTarget ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, measurableTarget: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Target Date" type="date" InputLabelProps={{ shrink: true }} fullWidth value={goalDialog?.targetDate ?? ''}
                onChange={e => setGoalDialog(p => ({ ...p, targetDate: e.target.value || undefined }))} />
              <TextField label="Weight %" type="number" sx={{ width: 120 }} value={goalDialog?.weight ?? 0}
                onChange={e => setGoalDialog(p => ({ ...p, weight: Number(e.target.value) }))} />
            </Box>
            <TextField select label="Status" fullWidth value={goalDialog?.status ?? 'not_started'}
              onChange={e => setGoalDialog(p => ({ ...p, status: e.target.value }))}>
              {['not_started','in_progress','met','not_met'].map(s => (
                <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
              ))}
            </TextField>
            <TextField label="Progress Notes" fullWidth multiline rows={2} value={goalDialog?.progressNotes ?? ''}
              onChange={e => setGoalDialog(p => ({ ...p, progressNotes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoalDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveGoal} disabled={!goalDialog?.goalTitle || !goalDialog?.description}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Check-in dialog */}
      <Dialog open={!!checkInDialog} onClose={() => setCheckInDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{checkInDialog?.id ? 'Edit Check-in' : 'Add Check-in'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Date" type="date" InputLabelProps={{ shrink: true }} fullWidth value={checkInDialog?.checkInDate ?? today}
              onChange={e => setCheckInDialog(p => ({ ...p, checkInDate: e.target.value }))} />
            <TextField select label="Rating" fullWidth value={checkInDialog?.rating ?? 'satisfactory'}
              onChange={e => setCheckInDialog(p => ({ ...p, rating: e.target.value }))}>
              {[
                { v: 'satisfactory', l: '✅ Satisfactory' },
                { v: 'needs_improvement', l: '⚠️ Needs Improvement' },
                { v: 'unsatisfactory', l: '❌ Unsatisfactory' },
              ].map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
            </TextField>
            <TextField label="Progress Summary" fullWidth required multiline rows={3} value={checkInDialog?.progressSummary ?? ''}
              onChange={e => setCheckInDialog(p => ({ ...p, progressSummary: e.target.value }))} />
            <TextField label="Concerns Raised" fullWidth multiline rows={2} value={checkInDialog?.concernsRaised ?? ''}
              onChange={e => setCheckInDialog(p => ({ ...p, concernsRaised: e.target.value }))} />
            <TextField label="Action Items" fullWidth multiline rows={2} value={checkInDialog?.actionItems ?? ''}
              onChange={e => setCheckInDialog(p => ({ ...p, actionItems: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel control={<Checkbox checked={checkInDialog?.employeePresent ?? false}
                onChange={e => setCheckInDialog(p => ({ ...p, employeePresent: e.target.checked }))} />} label="Employee present" />
              <FormControlLabel control={<Checkbox checked={checkInDialog?.employeeAgreed ?? false}
                onChange={e => setCheckInDialog(p => ({ ...p, employeeAgreed: e.target.checked }))} />} label="Employee agreed" />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckInDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveCheckIn} disabled={!checkInDialog?.progressSummary}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Outcome dialog */}
      <Dialog open={outcomeDialog} onClose={() => setOutcomeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record PIP Outcome</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField select label="Outcome" fullWidth value={outcome}
              onChange={e => setOutcome(e.target.value)}>
              {Object.entries(OUTCOME_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            <TextField label="Notes" fullWidth multiline rows={3} value={outcomeNotes}
              onChange={e => setOutcomeNotes(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutcomeDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={recordOutcome} disabled={!outcome}>Record</Button>
        </DialogActions>
      </Dialog>

      {/* Acknowledge dialog */}
      <Dialog open={ackDialog} onClose={() => setAckDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Acknowledge PIP</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            By acknowledging, you confirm you have read and understood this Performance Improvement Plan.
          </Typography>
          <TextField label="Your comments (optional)" fullWidth multiline rows={3} value={ackComment}
            onChange={e => setAckComment(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAckDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={acknowledge}>Acknowledge</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PIPPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = user?.role === 'manager';
  const currentEmpId = user?.employee?.id;

  const [tab, setTab] = useState(0);
  const [pips, setPips] = useState<Pip[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailPip, setDetailPip] = useState<Pip | null>(null);
  const [pipDialog, setPipDialog] = useState<Partial<Pip> | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const isEmployee = !isHR && !isManager;
      const [p, st] = await Promise.all([
        isEmployee ? apiFetch('/api/pip/my') : apiFetch('/api/pip'),
        (isHR || isManager) ? apiFetch('/api/pip/stats') : Promise.resolve(null),
      ]);
      setPips(p); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [isHR, isManager]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if ((isHR || isManager) && employees.length === 0)
      apiFetch('/api/employees?limit=500').then(r => setEmployees(r.data || r || [])).catch(() => {});
  }, [isHR, isManager, employees.length]);

  const savePip = async () => {
    if (!pipDialog) return;
    try {
      pipDialog.id
        ? await apiFetch(`/api/pip/${pipDialog.id}`, { method: 'PATCH', body: JSON.stringify(pipDialog) })
        : await apiFetch('/api/pip', { method: 'POST', body: JSON.stringify(pipDialog) });
      setPipDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const deletePip = async (id: string) => {
    if (!window.confirm('Delete this PIP? This cannot be undone.')) return;
    try { await apiFetch(`/api/pip/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const openDetail = async (pip: Pip) => {
    try {
      const full = await apiFetch(`/api/pip/${pip.id}`);
      setDetailPip(full);
    } catch (e: any) { setError(e.message); }
  };

  const filterByTab = (list: Pip[]) => {
    if (tab === 0) return list.filter(p => p.status === 'active' || p.status === 'extended');
    if (tab === 1) return list.filter(p => p.status === 'draft');
    if (tab === 2) return list.filter(p => ['passed','failed','withdrawn'].includes(p.status));
    return list;
  };

  const filtered = filterByTab(pips);
  const overdue = pips.filter(p => (p.status === 'active' || p.status === 'extended') && p.endDate < today);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingDown sx={{ fontSize: 32, color: '#c62828' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Performance Improvement Plans</Typography>
            <Typography variant="body2" color="text.secondary">
              Structured support for employees needing performance guidance
            </Typography>
          </Box>
        </Box>
        {(isHR || isManager) && (
          <Button variant="contained" sx={{ bgcolor: '#c62828' }} startIcon={<Add />}
            onClick={() => setPipDialog({ status: 'draft', durationDays: 90, startDate: today })}>
            New PIP
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 4, sm: 2 }}>
            <StatCard label="Total" value={stats.total} color="#1565c0" icon={<Flag />} />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <StatCard label="Active" value={stats.active} color="#e65100" icon={<TrendingDown />} />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <StatCard label="Overdue" value={stats.overdue} color={stats.overdue > 0 ? '#c62828' : '#2e7d32'} icon={<Warning />} />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <StatCard label="Passed" value={stats.passed} color="#2e7d32" icon={<CheckCircle />} />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <StatCard label="Failed" value={stats.failed} color="#b71c1c" icon={<TrendingDown />} />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <StatCard label="Draft" value={stats.draft} color="#90a4ae" icon={<Person />} />
          </Grid>
        </Grid>
      )}

      {overdue.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography fontWeight={700}>⚠️ {overdue.length} PIP{overdue.length > 1 ? 's' : ''} past review date — outcome required</Typography>
          {overdue.map(p => (
            <Typography key={p.id} variant="body2">
              • {p.employee?.firstName} {p.employee?.lastName} — {p.title} (ended {fmtDate(p.endDate)})
            </Typography>
          ))}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`Active (${pips.filter(p => p.status === 'active' || p.status === 'extended').length})`} />
          <Tab label={`Draft (${pips.filter(p => p.status === 'draft').length})`} />
          <Tab label="Closed" />
          <Tab label={`All (${pips.length})`} />
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <TrendingDown sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography>No PIPs in this category.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fff3e0' }}>
                <TableCell><b>Employee</b></TableCell>
                <TableCell><b>PIP Title</b></TableCell>
                <TableCell><b>Period</b></TableCell>
                <TableCell><b>Progress</b></TableCell>
                <TableCell><b>Goals</b></TableCell>
                <TableCell><b>Check-ins</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Ack</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(p => {
                const active = p.status === 'active' || p.status === 'extended';
                const pct = active ? pctElapsed(p.startDate, p.endDate) : 0;
                const days = active ? daysLeft(p.endDate) : null;
                const goals = p.goals ?? [];
                const metGoals = goals.filter(g => g.status === 'met').length;

                return (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{p.employee?.firstName} {p.employee?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.employee?.department?.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{p.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmtDate(p.startDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">→ {fmtDate(p.endDate)}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {active ? (
                        <Box>
                          <LinearProgress variant="determinate" value={pct}
                            color={pct > 80 ? 'error' : pct > 50 ? 'warning' : 'success'}
                            sx={{ height: 6, borderRadius: 1, mb: 0.5 }} />
                          <Typography variant="caption" color={days! <= 0 ? 'error.main' : days! <= 14 ? 'warning.main' : 'text.secondary'}>
                            {days! > 0 ? `${days}d left` : `${Math.abs(days!)}d overdue`}
                          </Typography>
                        </Box>
                      ) : <Typography variant="caption" color="text.secondary">{p.durationDays}d</Typography>}
                    </TableCell>
                    <TableCell>
                      {goals.length > 0
                        ? <Chip label={`${metGoals}/${goals.length} met`} size="small"
                            color={metGoals === goals.length && goals.length > 0 ? 'success' : 'default'} />
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={p.checkIns?.length ?? 0} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={p.status} size="small" color={STATUS_COLORS[p.status] ?? 'default'} />
                    </TableCell>
                    <TableCell>
                      {p.employeeAcknowledged
                        ? <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                        : <Warning sx={{ color: 'warning.main', fontSize: 18 }} />}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openDetail(p)}>View</Button>
                      {isHR && (
                        <IconButton size="small" color="error" onClick={() => deletePip(p.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail dialog */}
      {detailPip && (
        <PipDetail
          pip={detailPip}
          onClose={() => { setDetailPip(null); load(); }}
          onRefresh={async () => {
            const fresh = await apiFetch(`/api/pip/${detailPip.id}`);
            setDetailPip(fresh);
            load();
          }}
          employees={employees}
          isHR={isHR}
          isManager={isManager}
          currentEmpId={currentEmpId}
        />
      )}

      {/* Create/Edit PIP dialog */}
      <Dialog open={!!pipDialog} onClose={() => setPipDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pipDialog?.id ? 'Edit PIP' : 'Create Performance Improvement Plan'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setPipDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField select label="Employee" fullWidth required value={pipDialog?.employeeId ?? ''}
              onChange={e => setPipDialog(p => ({ ...p, employeeId: e.target.value }))}>
              {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.department?.name ?? '—'})</MenuItem>)}
            </TextField>
            <TextField label="PIP Title" fullWidth required value={pipDialog?.title ?? ''}
              onChange={e => setPipDialog(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Sales Performance Improvement Plan Q2 2025" />
            <TextField label="Reason / Background" fullWidth required multiline rows={3}
              value={pipDialog?.reasonForPip ?? ''}
              onChange={e => setPipDialog(p => ({ ...p, reasonForPip: e.target.value }))}
              placeholder="Describe the performance issues that led to this PIP..." />
            <TextField label="Support Provided" fullWidth multiline rows={2} value={pipDialog?.supportProvided ?? ''}
              onChange={e => setPipDialog(p => ({ ...p, supportProvided: e.target.value }))}
              placeholder="Training, coaching, additional resources..." />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Date" type="date" InputLabelProps={{ shrink: true }} fullWidth value={pipDialog?.startDate ?? today}
                onChange={e => setPipDialog(p => ({ ...p, startDate: e.target.value }))} />
              <TextField select label="Duration" sx={{ width: 140 }} value={pipDialog?.durationDays ?? 90}
                onChange={e => setPipDialog(p => ({ ...p, durationDays: Number(e.target.value) }))}>
                {DURATION_OPTIONS.map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Manager" fullWidth value={pipDialog?.managerId ?? ''}
                onChange={e => setPipDialog(p => ({ ...p, managerId: e.target.value }))}>
                <MenuItem value="">— None —</MenuItem>
                {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
              </TextField>
              <TextField select label="HR Owner" fullWidth value={pipDialog?.hrOwnerId ?? ''}
                onChange={e => setPipDialog(p => ({ ...p, hrOwnerId: e.target.value }))}>
                <MenuItem value="">— None —</MenuItem>
                {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPipDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#c62828' }} onClick={savePip}
            disabled={!pipDialog?.employeeId || !pipDialog?.title || !pipDialog?.reasonForPip}>
            {pipDialog?.id ? 'Save' : 'Create PIP'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
